import { useVoting } from '../hooks/useVoting'
import { useVoteStats } from '../hooks/useVoteStats'
import { useAccount } from 'wagmi'
import { useEffect, useState } from 'react'

export default function Badges() {
  const { streak } = useVoting()
  const { address } = useAccount()
  const { bullishVotes, bearishVotes } = useVoteStats(address)
  const [hasEverVoted, setHasEverVoted] = useState(false)

  // Check if user has ever voted by checking on-chain streak
  useEffect(() => {
    if (!address) {
      setHasEverVoted(false)
      return
    }

    // User has voted if current streak > 0
    setHasEverVoted(streak > 0)
  }, [address, streak])

  const badges = [
    {
      id: 'first-vote',
      name: 'Vibe Checked',
      description: 'Your first on-chain sentiment',
      emoji: '✅',
      earned: hasEverVoted,
    },
    {
      id: 'week-warrior',
      name: 'Consistency Is Key',
      description: 'Vote 7 days in a row',
      emoji: '🔥',
      earned: streak >= 7,
    },
    {
      id: 'month-master',
      name: 'Diamond Hands',
      description: 'Vote 30 days in a row',
      emoji: '💎',
      earned: streak >= 30,
    },
    {
      id: 'century-champion',
      name: 'Based God',
      description: 'Vote 100 days in a row',
      emoji: '🧢',
      earned: streak >= 100,
    },
    // Bullish Tiers
    {
      id: 'bull-believer',
      name: 'Up Only',
      description: 'Vote bullish 10 times',
      emoji: '📈',
      earned: bullishVotes >= 10,
    },
    {
      id: 'bull-maxi',
      name: 'Permabull',
      description: 'Vote bullish 50 times',
      emoji: '🚀',
      earned: bullishVotes >= 50,
    },
    // Bearish Tiers
    {
      id: 'bear-brave',
      name: 'Short King',
      description: 'Vote bearish 10 times',
      emoji: '📉',
      earned: bearishVotes >= 10,
    },
    {
      id: 'bear-doomer',
      name: 'The Doomer',
      description: 'Vote bearish 50 times',
      emoji: '💀',
      earned: bearishVotes >= 50,
    },
    // Contrarian (Placeholder/Future)
    {
      id: 'contrarian-fade',
      name: 'Fade The Top',
      description: 'Vote Bearish when >80% are Bullish',
      emoji: '🧠',
      earned: false, // Requires historical sentiment tracking
    },
    {
      id: 'contrarian-catch',
      name: 'Knife Catcher',
      description: 'Vote Bullish when >80% are Bearish',
      emoji: '🩸',
      earned: false, // Requires historical sentiment tracking
    },
  ]

  return (
    <div className="pb-20 pt-2">
      <div className="container mx-auto px-3 max-w-2xl">
        <h2 className="text-xl font-bold text-white mb-4 text-center">
          Hall of Fame
        </h2>

        <div className="grid grid-cols-2 gap-3">
          {badges.map((badge) => (
            <div
              key={badge.id}
              className={`relative p-4 rounded-2xl border transition-all duration-300 ${badge.earned
                ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/30'
                : 'bg-white/5 border-white/5 opacity-50 grayscale'
                }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2 animate-float inline-block">
                  {badge.emoji}
                </div>
                <h3 className="font-bold text-sm text-white mb-1">
                  {badge.name}
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed">
                  {badge.description}
                </p>
                {badge.earned && (
                  <div className="absolute top-2 right-2">
                    <span className="flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Current Streak Display */}
        <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-2xl animate-pulse">🔥</div>
              <div>
                <h3 className="text-sm font-bold text-white">
                  {streak} Day Streak
                </h3>
                <p className="text-xs text-orange-400">
                  Keep the fire burning!
                </p>
              </div>
            </div>
            <div className="text-xs font-mono font-bold text-white/50 bg-white/5 px-2 py-1 rounded">
              Active
            </div>
          </div>
        </div>

        {/* Bull/Bear Vote Stats */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
            <div className="text-center">
              <div className="text-2xl mb-1">🐂</div>
              <h3 className="text-lg font-bold text-white">
                {bullishVotes}
              </h3>
              <p className="text-xs text-emerald-400 font-medium">
                Bullish Votes
              </p>
            </div>
          </div>
          <div className="p-3 rounded-2xl bg-gradient-to-br from-rose-500/10 to-pink-500/10 border border-rose-500/20">
            <div className="text-center">
              <div className="text-2xl mb-1">🐻</div>
              <h3 className="text-lg font-bold text-white">
                {bearishVotes}
              </h3>
              <p className="text-xs text-rose-400 font-medium">
                Bearish Votes
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

